import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Water Tank Cleaning Services in Gurugram | TankBuddy</title>
        <meta name="description" content="TankBuddy offers residential, overhead, underground and commercial water tank cleaning in Gurugram. Same-day service. Starting Rs. 999. Call +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/services" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            <MapPin className="w-4 h-4" /> Serving All Gurugram Sectors
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-4">
            Water Tank Cleaning Services in Gurugram
          </h1>
          <p className="text-slate-500 text-lg mb-10">
            Professional, certified and eco-friendly water tank cleaning for homes, apartments, societies and commercial properties across Gurugram.
          </p>

          <div className="bg-navy rounded-xl p-8 mb-12 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Tank Cleaning Today</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 mb-12">

            <a href="/residential-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">Residential Tank Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 999</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Complete cleaning for home water tanks. Safe for families, pets and plants.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark Overhead and underground tanks</li>
                <li>checkmark Family-safe eco-friendly products</li>
                <li>checkmark Completion in 2-3 hours</li>
                <li>checkmark 30-day hygiene guarantee</li>
              </ul>
            </a>

            <a href="/overhead-water-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">Overhead Tank Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 999</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Expert cleaning of rooftop tanks using high-pressure jet equipment.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark High-pressure jet cleaning</li>
                <li>checkmark Anti-bacterial treatment</li>
                <li>checkmark UV disinfection</li>
                <li>checkmark Safe rooftop access</li>
              </ul>
            </a>

            <a href="/underground-water-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">Underground Tank Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 1,499</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Deep cleaning of underground reservoirs including complete sludge removal.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark Complete sludge removal</li>
                <li>checkmark High-pressure wall scrubbing</li>
                <li>checkmark Full disinfection treatment</li>
                <li>checkmark Safety protocols followed</li>
              </ul>
            </a>

            <a href="/commercial-water-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">Commercial Tank Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 4,999</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Industrial-grade cleaning for offices, malls and commercial complexes.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark Industrial-grade equipment</li>
                <li>checkmark Compliance certificate</li>
                <li>checkmark Minimal downtime</li>
                <li>checkmark Quarterly plans available</li>
              </ul>
            </a>

            <a href="/residential-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">RWA Society Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 2,999</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Bulk cleaning packages for housing societies with special pricing.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark Multiple tanks in one visit</li>
                <li>checkmark Special bulk pricing</li>
                <li>checkmark Annual contract options</li>
                <li>checkmark Trusted by 50+ societies</li>
              </ul>
            </a>

            <a href="/residential-tank-cleaning-gurugram" className="bg-surface border border-slate-200 rounded-xl p-6 hover:border-sky/50 hover:shadow-md transition-all">
              <h2 className="font-heading font-bold text-lg text-navy mb-1">Same Day Emergency Cleaning</h2>
              <p className="text-sky font-semibold text-sm mb-3">Starting Rs. 1,499</p>
              <p className="text-slate-500 text-sm leading-relaxed mb-3">Urgent same-day service across all Gurugram sectors. Call for immediate booking.</p>
              <ul className="space-y-1 text-xs text-slate-600">
                <li>checkmark Same-day response</li>
                <li>checkmark All Gurugram sectors</li>
                <li>checkmark Certified technicians</li>
                <li>checkmark Available 7 days a week</li>
              </ul>
            </a>

          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Areas We Serve in Gurugram</h2>
            <div className="flex flex-wrap gap-2">
              <a href="/water-tank-cleaning-dlf-phase-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />DLF Phase 1-5</a>
              <a href="/water-tank-cleaning-sushant-lok-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sushant Lok</a>
              <a href="/water-tank-cleaning-palam-vihar-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Palam Vihar</a>
              <a href="/water-tank-cleaning-golf-course-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Golf Course Road</a>
              <a href="/water-tank-cleaning-sohna-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sohna Road</a>
              <a href="/water-tank-cleaning-mg-road-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />MG Road</a>
              <a href="/water-tank-cleaning-sector-49-50-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sector 49-50</a>
              <a href="/water-tank-cleaning-sector-56-57-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Sector 56-57</a>
              <a href="/water-tank-cleaning-nirvana-country-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Nirvana Country</a>
              <a href="/water-tank-cleaning-udyog-vihar-gurugram" className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors"><MapPin className="w-3 h-3 text-sky" />Udyog Vihar</a>
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
